import React, { useState, useEffect } from 'react';
import { 
  Database, 
  FileText, 
  Play, 
  Zap, 
  TrendingUp,
  Clock,
  CheckCircle
} from 'lucide-react';
import { versionsApi, testCasesApi, testsApi } from '../services/api';
import { AUTVersion, TestCase, TestStatistics } from '../types';

interface DashboardProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Dashboard: React.FC<DashboardProps> = ({ addNotification }) => {
  const [versions, setVersions] = useState<AUTVersion[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [statistics, setStatistics] = useState<TestStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  useEffect(() => {
    const orgIdStr = localStorage.getItem('selectedOrganizationId');
    const projectIdStr = localStorage.getItem('selectedProjectId');
    if (orgIdStr) {
      setSelectedOrganizationId(parseInt(orgIdStr));
    }
    if (projectIdStr) {
      setSelectedProjectId(parseInt(projectIdStr));
    }
  }, []);

  useEffect(() => {
    if (selectedOrganizationId && selectedProjectId) {
      // Add a small delay to avoid overwhelming the backend on initial page load
      const timer = setTimeout(() => {
        loadDashboardData();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [selectedOrganizationId, selectedProjectId]);

  const loadDashboardData = async () => {
    if (!selectedOrganizationId || !selectedProjectId) return;
    
    try {
      setLoading(true);
      
      // Helper function to retry on network errors
      const retryOnNetworkError = async <T,>(fn: () => Promise<T>, retries = 2): Promise<T> => {
        try {
          return await fn();
        } catch (error: any) {
          if ((error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') && retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            return retryOnNetworkError(fn, retries - 1);
          }
          throw error;
        }
      };
      
      // Load data with retry logic for network errors
      const [versionsData, testCasesData, statsData] = await Promise.all([
        retryOnNetworkError(() => versionsApi.list(selectedOrganizationId, selectedProjectId)).catch(() => [] as AUTVersion[]),
        retryOnNetworkError(() => testCasesApi.list(selectedOrganizationId, selectedProjectId)).catch(() => [] as TestCase[]),
        retryOnNetworkError(() => testsApi.getStatistics(selectedOrganizationId, selectedProjectId)).catch(() => null)
      ]);
      
      setVersions(versionsData || []);
      setTestCases(testCasesData || []);
      setStatistics(statsData);
    } catch (error) {
      addNotification('Failed to load dashboard data', 'error');
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusText = (score: number) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Fair';
    return 'Poor';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h1 className="text-2xl font-bold text-slate-100">Dashboard</h1>
        <p className="text-slate-300 mt-2">Overview of your HIVEAIASSURE system</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Versions */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-400">Versions</p>
              <p className="text-2xl font-semibold text-slate-100">{versions.length}</p>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-slate-400">
              {versions.filter(v => v.is_active).length} active
            </span>
          </div>
        </div>

        {/* Test Cases */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-400">Test Cases</p>
              <p className="text-2xl font-semibold text-slate-100">{testCases.length}</p>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-slate-400">
              {testCases.filter(tc => tc.source_type === 'pdf').length} from PDFs
            </span>
          </div>
        </div>

        {/* Test Executions */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Play className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-400">Tests Run</p>
              <p className="text-2xl font-semibold text-slate-100">
                {statistics?.test_executions.total_executions || 0}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-slate-400">
              {statistics?.test_executions.success_rate.toFixed(1) || 0}% success rate
            </span>
          </div>
        </div>

        {/* Average Score */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Zap className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-400">Avg Score</p>
              <p className="text-2xl font-semibold text-slate-100">
                {statistics?.evaluations.average_overall_score.toFixed(2) || 0}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(statistics?.evaluations.average_overall_score || 0)}`}>
              {getStatusText(statistics?.evaluations.average_overall_score || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <h3 className="text-lg font-medium text-slate-100 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {statistics && statistics.test_executions.total_executions > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center text-sm text-slate-300">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Last test run: {statistics.test_executions.average_execution_time.toFixed(2)}s avg</span>
                </div>
                <div className="flex items-center text-sm text-slate-300">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  <span>Successful tests: {statistics.test_executions.total_executions}</span>
                </div>
                <div className="flex items-center text-sm text-slate-300">
                  <TrendingUp className="h-4 w-4 mr-2 text-blue-500" />
                  <span>Total tokens used: {statistics.test_executions.total_tokens}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Play className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No tests run yet</p>
                <p className="text-sm">Start by running your first test</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <h3 className="text-lg font-medium text-slate-100 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              <Play className="h-4 w-4 mr-2" />
              Run New Test
            </button>
            <button className="w-full flex items-center justify-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              <FileText className="h-4 w-4 mr-2" />
              Upload PDF
            </button>
            <button className="w-full flex items-center justify-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              <Database className="h-4 w-4 mr-2" />
              Create Version
            </button>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-100 mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center p-3 bg-green-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">Backend API</span>
          </div>
          <div className="flex items-center p-3 bg-green-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">Database</span>
          </div>
          <div className="flex items-center p-3 bg-green-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">OpenAI API</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
